import { useEffect, useRef, useState } from 'react'
import type { MomentView } from '../types'

// ============================================================
// ActivePlayer — 單一 persistent YouTube iframe（矩陣化控制）
// ------------------------------------------------------------
// 架構原則：
//   - 整個 feed 只有「一個」iframe，建立後不銷毀
//   - moment.id 變化 → loadVideoById 切換內容（YT API 級別）
//   - 舊影片自動被蓋掉、新影片自動從 startSeconds 開始
//   - 不再靠 React mount/unmount 去控制誰在播
//
// 為什麼這樣比「每張卡自己的 YouTubePlayer」好？
//   - 切卡時 iframe destroy→create 要 1-2s，autoplay 常失敗
//   - 多 iframe 並存吃 memory（mobile 尤其容易 crash）
//   - 單一 source of truth：activeMomentId 就是答案
// ============================================================

type YTPlayer = {
  loadVideoById: (arg: {
    videoId: string
    startSeconds?: number
    endSeconds?: number
  }) => void
  playVideo: () => void
  pauseVideo: () => void
  seekTo: (seconds: number, allowSeekAhead: boolean) => void
  mute: () => void
  unMute: () => void
  getCurrentTime: () => number
  destroy: () => void
}

declare global {
  interface Window {
    YT?: {
      Player: new (
        el: HTMLElement,
        opts: {
          videoId: string
          playerVars?: Record<string, string | number>
          events?: {
            onReady?: (e: { target: YTPlayer }) => void
            onStateChange?: (e: { data: number }) => void
          }
        }
      ) => YTPlayer
      PlayerState: { ENDED: 0; PLAYING: 1; PAUSED: 2; BUFFERING: 3 }
    }
    onYouTubeIframeAPIReady?: () => void
  }
}

let apiLoadPromise: Promise<void> | null = null

function loadYouTubeAPI(): Promise<void> {
  if (apiLoadPromise) return apiLoadPromise
  apiLoadPromise = new Promise((resolve) => {
    if (window.YT?.Player) return resolve()
    const tag = document.createElement('script')
    tag.src = 'https://www.youtube.com/iframe_api'
    document.head.appendChild(tag)
    const prev = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      prev?.()
      resolve()
    }
  })
  return apiLoadPromise
}

interface Props {
  moment: MomentView | null
  muted: boolean
  /** 由 header player 控制 — true 時呼叫 pauseVideo，false 時 playVideo */
  paused?: boolean
  onEnded?: () => void
  /** 改變時 → seek 回 startSeconds 重播（unmute 時用）。初始 0 不觸發。 */
  replayNonce?: number
  presentation?: 'feed' | 'background'
}

export function ActivePlayer({
  moment,
  muted,
  paused = false,
  onEnded,
  replayNonce = 0,
  presentation = 'feed'
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<YTPlayer | null>(null)
  const [ready, setReady] = useState(false)
  const [playerState, setPlayerState] = useState<number>(-1)
  const [showPlayHint, setShowPlayHint] = useState(false)
  const lastLoadedIdRef = useRef<string | null>(null)
  // 給 onStateChange callback 用 — callback 閉包早期 paused 值，要靠 ref 拿最新值
  const pausedRef = useRef(paused)
  useEffect(() => {
    pausedRef.current = paused
  }, [paused])

  // 建立 player — 只跑一次
  useEffect(() => {
    let cancelled = false
    loadYouTubeAPI().then(() => {
      if (cancelled || !containerRef.current || !window.YT) return
      const initialVideoId = moment?.youtubeId ?? 'C3VIxj-lXRQ'
      const initialStart = moment?.startSeconds ?? 0
      lastLoadedIdRef.current = moment?.id ?? null
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: initialVideoId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0,
          start: Math.floor(initialStart)
        },
        events: {
          onReady: (e) => {
            setReady(true)
            try {
              e.target.mute()
              e.target.playVideo()
            } catch {
              /* ignore */
            }
          },
          onStateChange: (e) => {
            setPlayerState(e.data)
            // CUED = 載入完但停著 → 補一刀 play（某些 browser loadVideoById 後停這裡）
            // 但若 user 已 pause 過，就尊重 pause 不要硬播
            if (e.data === 5 && !pausedRef.current) {
              try {
                playerRef.current?.playVideo()
              } catch {
                /* ignore */
              }
            }
            // PLAYING 時若實際是 paused state（autoplay 搶先 fire）→ 馬上 pause
            if (e.data === 1 && pausedRef.current) {
              try {
                playerRef.current?.pauseVideo()
              } catch {
                /* ignore */
              }
            }
            if (e.data === window.YT?.PlayerState.ENDED) onEnded?.()
          }
        }
      })
    })
    return () => {
      cancelled = true
      playerRef.current?.destroy()
      playerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 核心：moment.id 變化 → loadVideoById，不重建 iframe
  useEffect(() => {
    if (!ready || !playerRef.current || !moment) return
    if (lastLoadedIdRef.current === moment.id) return // 同一 moment 不重 load
    lastLoadedIdRef.current = moment.id
    try {
      playerRef.current.loadVideoById({
        videoId: moment.youtubeId,
        startSeconds: moment.startSeconds,
        endSeconds: moment.endSeconds
      })
      // 保險：某些 browser（尤其 iOS）不會依 loadVideoById 自動 play
      // explicit 再打一次
      playerRef.current.playVideo()
    } catch {
      /* ignore — API 尚未就緒等下個 effect */
    }
  }, [ready, moment?.id, moment?.youtubeId, moment?.startSeconds, moment?.endSeconds])

  // muted 同步 — 使用者按 header 音量 / 卡片「點此解除靜音」都會走這
  useEffect(() => {
    if (!ready || !playerRef.current) return
    try {
      muted ? playerRef.current.mute() : playerRef.current.unMute()
    } catch {
      /* ignore */
    }
  }, [muted, ready])

  // paused 同步 — header 的 ▶/⏸ 按鈕透過這個切換。
  // 注意：loadVideoById 之後 iframe 會自己 PLAYING；若此時 paused=true，需要再 pause 一刀。
  // 監聽 moment?.id 確保切卡片後也尊重當前的 paused 旗標。
  useEffect(() => {
    if (!ready || !playerRef.current) return
    try {
      paused ? playerRef.current.pauseVideo() : playerRef.current.playVideo()
    } catch {
      /* ignore */
    }
  }, [paused, ready, moment?.id])

  // replay：unmute 時父層會 bump nonce → 把播放位置拉回 startSeconds 重聽「那一刻」
  useEffect(() => {
    if (!ready || !playerRef.current || !moment || replayNonce === 0) return
    try {
      playerRef.current.seekTo(moment.startSeconds, true)
      playerRef.current.playVideo()
    } catch {
      /* ignore */
    }
    // 刻意不監聽 moment — 只有 nonce 變才觸發，避免跟 loadVideoById effect 打架
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayNonce])

  // 1.5s 還沒 PLAYING / BUFFERING → autoplay 被擋，露出手動播放按鈕
  useEffect(() => {
    if (playerState === 1 || playerState === 3) {
      setShowPlayHint(false)
      return
    }
    const id = window.setTimeout(() => setShowPlayHint(true), 1500)
    return () => window.clearTimeout(id)
  }, [playerState, moment?.id])

  const manualPlay = () => {
    if (!playerRef.current || !moment) return
    playerRef.current.seekTo(moment.startSeconds, true)
    playerRef.current.playVideo()
    setShowPlayHint(false)
  }

  // endSeconds 守門（有設才啟用）
  useEffect(() => {
    if (!ready || !playerRef.current || !moment?.endSeconds) return
    const end = moment.endSeconds
    const id = window.setInterval(() => {
      const t = playerRef.current?.getCurrentTime() ?? 0
      if (t >= end) {
        playerRef.current?.pauseVideo()
        onEnded?.()
        window.clearInterval(id)
      }
    }, 500)
    return () => window.clearInterval(id)
  }, [ready, moment?.id, moment?.endSeconds, onEnded])

  // PLAYING 時 poster 淡出；其他狀態（UNSTARTED / CUED / BUFFERING / PAUSED）都蓋著
  // 這一層是為了遮掉 YouTube iframe load 新 video 時自己顯示的紅色 ▶ 海報
  const posterVisible = playerState !== 1

  const isFeed = presentation === 'feed'

  // feed：像影片作品置中觀看；background：全螢幕 contain。
  const containStyle = {
    width: isFeed ? 'min(92vw, 1180px, calc((100dvh - 13rem) * 16 / 9))' : 'min(100vw, calc(100dvh * 16 / 9))',
    aspectRatio: '16 / 9'
  } as const

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden bg-ink">
      {/* Ambient blur 背景 — 該卡縮圖高斯模糊 + 加深，填滿 letterbox 區 */}
      {moment && (
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center transition-opacity duration-500"
          style={{
            backgroundImage: `url(https://i.ytimg.com/vi/${moment.youtubeId}/maxresdefault.jpg)`,
            filter: isFeed
              ? 'blur(52px) brightness(0.42) saturate(135%)'
              : 'blur(40px) brightness(0.55) saturate(110%)',
            transform: isFeed ? 'scale(1.22)' : 'scale(1.15)'
          }}
        />
      )}

      {isFeed && (
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/35 to-ink/85"
        />
      )}

      {/* Contain 影片 box — 16:9 居中，永不裁切 */}
      <div
        className={
          isFeed
            ? 'absolute inset-x-0 top-[5.75rem] flex justify-center px-4 md:top-[6.75rem]'
            : 'absolute inset-0 flex items-center justify-center'
        }
      >
        <div
          className={
            isFeed
              ? 'relative overflow-hidden rounded-lg bg-ink shadow-2xl ring-1 ring-paper/15'
              : 'relative'
          }
          style={containStyle}
        >
          <div ref={containerRef} className="h-full w-full" />

          {/* 縮圖 poster — 與影片同 box，遮掉 iframe 載入期間的紅色 ▶ */}
          {moment && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 overflow-hidden bg-ink transition-opacity duration-300"
              style={{ opacity: posterVisible ? 1 : 0 }}
            >
              <img
                key={moment.youtubeId}
                src={`https://i.ytimg.com/vi/${moment.youtubeId}/maxresdefault.jpg`}
                onError={(e) => {
                  ;(e.currentTarget as HTMLImageElement).src = `https://i.ytimg.com/vi/${moment.youtubeId}/hqdefault.jpg`
                }}
                alt=""
                className="h-full w-full object-cover"
                referrerPolicy="no-referrer"
              />
            </div>
          )}
        </div>
      </div>

      {showPlayHint && moment && (
        <button
          onClick={manualPlay}
          className="pointer-events-auto absolute left-1/2 top-1/2 z-20 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-ink/55 text-paper backdrop-blur transition hover:bg-ink/75 active:scale-95"
          aria-label="播放"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M6 4 L16 10 L6 16 Z" />
          </svg>
        </button>
      )}
    </div>
  )
}
